import type { ReviewWallTabId } from "@/views/shop/shopContent";

type CachedTweets = typeof import("../../../cache/tweets.json");
type CachedTweetId = Extract<keyof CachedTweets, string>;

// Ported verbatim from the legacy content/CustomerReviews.ts (master), with
// the six legacy categories editorially mapped onto the five redesign tabs:
// General love + Customer support → General Love; Kids (+ toy/cable-safety
// quotes) → Families; Cleaning + Obstacle avoidance → Time Saved.

export type ReviewWallTextReviewCard = {
  kind: "review";
  quote: string;
  name: string;
  customerSince: string;
};

export type ReviewWallTweetCard = {
  kind: "tweet";
  /** Key into cache/tweets.json. */
  tweetId: CachedTweetId;
};

export type ReviewWallPortedCard =
  ReviewWallTextReviewCard | ReviewWallTweetCard;

export const REVIEW_WALL_REVIEWS: Record<
  ReviewWallTabId,
  readonly ReviewWallPortedCard[]
> = {
  "general-love": [
    {
      kind: "review",
      quote:
        "You all are amazing. I'm going to need about five for Christmas gifts, not kidding",
      name: "Dave C.",
      customerSince: "April 2025",
    },
    {
      kind: "review",
      quote:
        "I'm a happy Matic owner - the vacuum has been excellent and I love the privacy-first approach (as a tech company CEO myself, I really appreciate that you process everything on-device).",
      name: "Alex F.",
      customerSince: "July 2025",
    },
    // Shane Parrish's tweet is the curated shared card on this tab — porting
    // it here again would render it twice.
    {
      kind: "review",
      quote:
        "We really loved how easy unboxing and set up was! Vacuuming has so far been wonderful.",
      name: "Nauka K.",
      customerSince: "December 2025",
    },
    {
      kind: "review",
      quote:
        "My Matic vacuum has been incredible, and I have been telling everyone about it.",
      name: "Benjamin R.",
      customerSince: "Oct 2025",
    },
    {
      kind: "review",
      quote:
        "I just got a second matic for another floor of my house since I love the first.",
      name: "Austin M.",
      customerSince: "December 2025",
    },
    {
      kind: "review",
      quote: "Privacy without compromising functionality and usability.",
      name: "Lucien D.",
      customerSince: "March 2025",
    },
    {
      kind: "review",
      quote:
        "Matic is the best robot vacuum on the market and I just love watching it clean. With its googly eyes on of course.",
      name: "Frank C.",
      customerSince: "March 15 2025",
    },
    {
      kind: "review",
      quote:
        "I am a huge fan of what you all are doing both as an engineer and a customer",
      name: "Brian W.",
      customerSince: "November 2025",
    },
    {
      kind: "review",
      quote:
        "I had been researching it for a while and am so admiring of you and your team's drive to make this product (and brand) remarkable. I love supporting brands like that.",
      name: "Travis S.",
      customerSince: "Nov 2025",
    },
    {
      kind: "review",
      quote:
        "Matic has arrived! This guy is amazing! As a software developer, I must say kudos to the team!!",
      name: "Greg P.",
      customerSince: "Nov 2025",
    },
    {
      kind: "review",
      quote:
        "Love the product. Best smart device we have owned. It legitimately helps with day to day use vs adding more work and maintenance.",
      name: "Jeffrey K.",
      customerSince: "Oct 2025",
    },
    {
      kind: "review",
      quote:
        "Every Matic update has been better than the last. The reliability and stability has really leveled out. I'm glad I stuck with some early bumps - genuinely a great product.",
      name: "Tommy H.",
      customerSince: "March 2025",
    },
    {
      kind: "review",
      quote:
        "I cannot give you guys enough kudos for your great service and great response. Thank you",
      name: "Robert U.",
      customerSince: "October 2025",
    },
    {
      kind: "review",
      quote:
        "When I did have an issue the team took care of it quickly. I had my Matic back within 10 days. Every step of the way they kept me informed of the progress and explained exactly what happened with my unit. I waited for Matic specifically to introduce a robot vacuum to my house and I definitely made the right decision.",
      name: "Chad W.",
      customerSince: "Nov 2023",
    },
    {
      kind: "review",
      quote: "Over the top, personal customer service.",
      name: "Joe E.",
      customerSince: "Nov 2023",
    },
    {
      kind: "review",
      quote:
        "EXCELLENT customer service! Real determination to make the product better and take input from users. The bot has gotten better over time, and issues I run into are handled very quickly.",
      name: "Michael H.",
      customerSince: "Nov 2023",
    },
    {
      kind: "review",
      quote:
        "I can't tell you how refreshing it is to have a corporation so responsive its customers.",
      name: "Joseph V.",
      customerSince: "Jan 2026",
    },
    {
      kind: "review",
      quote:
        "The customer service I've received this entire time has been stellar. The support team has always gotten to my tickets fairly quickly and the improvements from day one to today are noticeable.",
      name: "Dustin M.",
      customerSince: "March 2025",
    },
    {
      kind: "review",
      quote:
        "I really didn't expect a response on Thanksgiving Day, never mind three. That is a compliment to you and the company.",
      name: "David S.",
      customerSince: "December 2025",
    },
  ],
  pets: [
    { kind: "tweet", tweetId: "1931406344050937919" },
    {
      kind: "review",
      quote:
        "I have to say this is a game changer. I don't have left over hair on the flap that is right behind the roller and my floors look amazing. No hair to be found. I ran it three different days. This is a wonderful thing for pet owners.",
      name: "Wayne N.",
      customerSince: "Dec 2023",
    },
    { kind: "tweet", tweetId: "1988734245280575755" },
    { kind: "tweet", tweetId: "1869968440980910545" },
    { kind: "tweet", tweetId: "2016949168024273380" },
    {
      kind: "review",
      quote:
        "Matic has been a game changer in my house. I have six cats so the floors are always a disaster. I work full time and run a business from home so it could be hard to find time to vacuum daily. It takes just a few seconds to change the bag or add some water and Matic does everything else. When I give it a deep clean once a month Matic comes apart and goes back together easily.",
      name: "Chad W.",
      customerSince: "Nov 2023",
    },
    {
      kind: "review",
      quote:
        "Our dogs(and kids) obviously are more comfortable around Matic, they know - even the dogs, it's not going to hit them. Which has not been the experience with other vacuums.",
      name: "Matthew K.",
      customerSince: "May 2025",
    },
    {
      kind: "review",
      quote:
        "You guys nailed it! My German shepherds shed so much, I literally need to vacuum daily to keep visible hair balls from tumbling down the hall. Roomba could not cope with the hair",
      name: "Bri P.",
      customerSince: "May 2025",
    },
    { kind: "tweet", tweetId: "1956027327617356282" },
  ],
  families: [
    {
      kind: "review",
      quote:
        "I absolutely love my matic robot. It's the first time I've had a robot vacuum that can operate in the chaos of having 3 kids and 3 dogs",
      name: "Adam L.",
      customerSince: "Oct 2025",
    },
    {
      kind: "review",
      quote:
        "Hi Matic, I am absolutely loving my Matic. I was a Roomba enthusiast for years and eventually got extremely frustrated with the solutions for a multi-floor house. The experience from all robotic vacuums, especially due to the newer designs of their very complicated docks means a user with a multi-floor home basically has a choice. Either you have to babysit the robot and move it to a new floor and wait until it finishes and spend your whole day dealing with it, or you are essentially forced to buy one robot per floor which I did end up doing, but not only is that extremely expensive, but now you have maintenance on multiple robots (which historically had way more maintenance than Matic which maintenance is so minimal!). With Matic, one robot does the whole house!",
      name: "Christian S.",
      customerSince: "June 2025",
    },
    {
      kind: "review",
      quote:
        "My parents have difficulty with manual floor care tools and Matic is a real win for them.",
      name: "Sean S.",
      customerSince: "Nov 2025",
    },
    {
      kind: "review",
      quote:
        "I knew my wife would eventually see the magic of having clean floors every morning and not have to worry about the baby crawling dirty floors. She absolutely loves this thing and raves about it to her friends now.",
      name: "Travis S.",
      customerSince: "May 2025",
    },
    { kind: "tweet", tweetId: "2019114699548422473" },
    {
      kind: "review",
      quote:
        "My kids are obsessed with it. Highly recommend for anyone with busy lifestyle or perpetually clean floors.",
      name: "Daniel G.",
      customerSince: "Nov 2025",
    },
    { kind: "tweet", tweetId: "2047479051045224552" },
    {
      kind: "review",
      quote:
        "Something good to note, not once, since owning my Matic has it ever tried to vacuum a cable, wire or sock. Really impressive considering my iRobot Roomba j9 would actively go hunting for wires and stuff to chew on!",
      name: "Clive R.",
      customerSince: "August 2025",
    },
    {
      kind: "review",
      quote:
        "The first time a scheduled clean worked, even after I forgot to clean up a mess of toys in the living room, was a true a-ha moment.",
      name: "Richard B.",
      customerSince: "January 2026",
    },
  ],
  "work-from-home": [
    { kind: "tweet", tweetId: "1931737238460874858" },
    {
      kind: "review",
      quote:
        "Because Matic is so quiet, I never mind running him. He cleans upstairs at night while we sleep downstairs. If I have been busy in the kitchen, I send him in after I'm done.\nHe has never locked himself in the bathroom, which Roomba used to do all the time.\nMy floors are so much cleaner - we have tile, wood, area rugs and some carpeting - he cleans it all so well.",
      name: "Hilary S.",
      customerSince: "November 2025",
    },
  ],
  "time-saved": [
    {
      kind: "review",
      quote: "Edge & Toe-kick Cleaning are game changers. Thank you!",
      name: "Taro F.",
      customerSince: "March 2025",
    },
    { kind: "tweet", tweetId: "1963042928143593658" },
    {
      kind: "review",
      quote:
        "We recently rearranged some furniture in one room to make space for our Christmas tree and haven't remapped the area yet since the change was temporary. That said, it has still successfully cleaned the space in its current layout without issue.\nWe're only running it twice a week, and our floors are staying spotless. We're excited enough that we're planning to buy a second unit for the other floor!",
      name: "Gregory P.",
      customerSince: "Nov 2025",
    },
    {
      kind: "review",
      quote:
        "I am absolutely thrilled to share how amazing this vacuum/mop is! From the room mapping to its impressive runtime, it truly is outstanding! I adore the visual representation of rooms instead of just simple lines. It does an exceptional job at cleaning! This is simply the best!",
      name: "Nancy S.",
      customerSince: "November 2025",
    },
    {
      kind: "review",
      quote:
        "The Matic has been wonderful - and in my home I have hardwood, tile, and carpet floors.",
      name: "Brandon B.",
      customerSince: "March 2025",
    },
    {
      kind: "review",
      quote: "Great at finding its way around and a good vacuum and mop.",
      name: "Ben O.",
      customerSince: "Nov 2023",
    },
    {
      kind: "review",
      quote:
        "Matic is the first true functional household cleaning robot. It's been very impressive.",
      name: "Jeremy S.",
      customerSince: "Nov 2025",
    },
    {
      kind: "review",
      quote:
        "The combination of powerful vacuuming and thorough mopping in one robot has made floor cleaning something we no longer dread - it's actually satisfying now.",
      name: "Bruce S.",
      customerSince: "November 2025",
    },
    {
      kind: "review",
      quote:
        "Had our Matic something over 6 months now and it's really amazing (I have a physics & software background)... watching it navigate and deal with obstacles and dynamic situations is mind blowing.",
      name: "John P.",
      customerSince: "May 2025",
    },
    {
      kind: "review",
      quote:
        "After using the robot for a few days, I've noticed that it has superior navigation and obstacle avoidance compared to other \"leading\" robot vacuums I've owned over the years",
      name: "Hamlik A.",
      customerSince: "September 2025",
    },
    {
      kind: "review",
      quote:
        "The mapping was unbelievably fast and accurate right from the first run, and watching it navigate is almost magical. It never gets lost, never bumps around confused, and returns to the dock perfectly every time.",
      name: "Bruce S.",
      customerSince: "November 2025",
    },
  ],
};

// Every card must be unique across the whole wall: the curated shared cards
// live in shopContent, so a ported entry may not repeat a tweet or a quote —
// paging surfaces every card, and a duplicate ships silently otherwise.
const portedCards = Object.values(REVIEW_WALL_REVIEWS).flat();
const portedTweetIds = portedCards
  .filter((card) => card.kind === "tweet")
  .map((card) => card.tweetId);
const portedQuotes = portedCards
  .filter((card) => card.kind === "review")
  .map((card) => card.quote);
const findDuplicate = (values: readonly string[]) =>
  values.find((value, index) => values.indexOf(value) !== index);
for (const [label, duplicate] of [
  ["tweet", findDuplicate(portedTweetIds)],
  ["quote", findDuplicate(portedQuotes)],
] as const) {
  if (duplicate !== undefined) {
    throw new Error(
      `Duplicate review wall ${label}: ${duplicate.slice(0, 60)}`,
    );
  }
}
